import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { Loading } from '@shoflo/polymer/components/Loader/Loader';
import AppContext from '@shoflo/polymer/contexts/AppContext';
import BrandContext from '@shoflo/polymer/contexts/BrandContext';
import useLaunchDarkly from '@shoflo/polymer/hooks/useLaunchDarkly/useLaunchDarkly';
import { getActiveSourceForUser } from '@shoflo/polymer/utils/broadcastSourceHelper/broadcastSourceHelper';
import { MetricsProvider } from '@studio/metrics/client/MetricsProvider';
import { LoadReadinessCheckerSLIObserver } from '@studio/metrics/components/LoadReadinessCheckerSLIObserver';
import { ATTRIBUTE_PERSONA_TYPE_HOST } from '@studio/metrics/constants/PersonaType';
import { getEnvVar } from '@studio/environment/getEnvVar';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { broadcastTypesMap } from '@studio/constants/BroadcastTypes';
import { getEnvironmentType } from '@studio/constants/EnvironmentTypes';

import Modals from './App.modals';
import Paths from 'constants/AppPaths';
import getCurrentBroadcast from '@shoflo/studio-redux/actions/broadcast/getCurrentBroadcast';
import getSources from '@shoflo/studio-redux/actions/source/getSources';
import { useLDPayload } from '@shoflo/studio-redux/hooks/useLDPayload/useLDPayload';
import { useRequireAuth } from '@shoflo/studio-redux/hooks/useRequireAuth/useRequireAuth';
import getTeam from '@shoflo/studio-redux/actions/team/getTeam';
import getEvent from '@shoflo/studio-redux/actions/event/getEvent';
import { getBroadcastRolesWithUsers } from '@shoflo/studio-redux/actions/broadcastRoles/getBroadcastRolesWithUsers';
import { useTokenValidationOnFocus } from '@shoflo/studio-redux/utils/useTokenValidationOnFocus';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getURLPathParams } from '@studio/logger/utils/getURLPathParams';
import packageJSON from '../package.json';

const { version } = packageJSON;

// Routes whose entry starts the "load readiness checker" page-load SLI.
const LOAD_READINESS_SLI_PATHS = [Paths.JoinPage];

const AgoraBroadcastPage = lazy(() =>
  import('containers/pages/Broadcast/AgoraBroadcast')
);
const AgoraJoin = lazy(() => import('./containers/pages/Join/Agora/AgoraJoin'));

const IntercomApp = lazy(() =>
  import('@studio/intercom/IntercomApp').then(module => ({
    default: module.default
  }))
);

const appContextValue = { isStudioLite: true };

const intercomProps = {
  appId: getEnvVar('REACT_APP_INTERCOM_APP_ID'),
  intercomIdentityVerificationKey: getEnvVar(
    'REACT_APP_INTERCOM_IDENTITY_VERIFICATION_KEY'
  )
};

const useDatadogGlobalProperties = ({
  userId,
  user,
  externalUserId,
  broadcast,
  accountMappingId
}) => {
  useEffect(() => {
    if (userId) {
      datadogRum.setUser({ id: userId });
      datadogRum.setGlobalContextProperty('studio.user.id', userId);
      datadogLogs.setGlobalContextProperty('studio.user.id', userId);

      /**
       * TODO: Fix to identify users is to move to the new @cvent/analytics SDK once we have ways to migrate on it.
       * Tracking Ticket: https://jira.cvent.com/browse/WEBINAR-27609
       * It's us who are adding the RudderAnalytics in the window object.
       * https://github.com/shoflo-internal/metrics/blob/main/src/client/initRudderstack.js#L79.
       */
      // identify user profile
      window?.rudderanalytics?.identify(
        userId,
        {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          userId,
          persona: ATTRIBUTE_PERSONA_TYPE_HOST
        },
        {
          integrations: { All: false, Intercom: true, Mixpanel: true }
        }
      );
    }
  }, [userId, user]);

  useEffect(() => {
    if (externalUserId) {
      datadogRum.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
    }
  }, [externalUserId]);

  useEffect(() => {
    if (broadcast?.externalId) {
      datadogRum.setGlobalContextProperty(
        'studio.webinar.id',
        broadcast.externalId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.webinar.id',
        broadcast.externalId
      );
    }
  }, [broadcast]);

  useEffect(() => {
    if (accountMappingId) {
      datadogRum.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
      datadogLogs.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
    }
  }, [accountMappingId]);
};

function App() {
  const user = useSelector(state => state.user, shallowEqual);
  const broadcast = useSelector(state => state.broadcast, shallowEqual);
  const accountMappingId = useSelector(state => state.team?.externalId);
  const brandingSettings = useSelector(
    state => state.brandingSettings,
    shallowEqual
  );
  const externalEventId = useSelector(state => state.event?.externalId);
  const eventTitle = useSelector(state => state.event?.title);
  const eventStartDate = useSelector(state => state.event?.startOn);
  const eventEndDate = useSelector(state => state.event?.endOn);

  const userId = user && user._id;
  const sourceId = useSelector(state => {
    const broadcastSources = state.broadcastSources;
    const activeSource = broadcastSources;
    getActiveSourceForUser(broadcastSources, userId);
    return activeSource?._id;
  });
  const externalUserId = user && user.externalId;

  const hasAttendeeCheckedReadiness = useSelector(
    state => state.attendeeReadinessCheck?.hasAttendeeCheckedReadiness,
    shallowEqual
  );
  useRequireAuth();
  const dispatch = useDispatch();

  // Check if all data required for LaunchDarkly is loaded
  // usLDPayload needs: user, team, event, and broadcast
  const teamIdExists = useSelector(state => state.team?._id);
  const eventIdExists = useSelector(state => state.event?._id);
  const broadcastIdExists = useSelector(state => state.broadcast?._id);

  const isRequiredDataLoaded =
    !!teamIdExists && !!eventIdExists && !!broadcastIdExists;

  useDatadogGlobalProperties({
    userId,
    user,
    externalUserId,
    broadcast,
    accountMappingId
  });

  /**
   *  Event listener for tab focus: We use this to confirm the user is still
   * authenticated and authorised when the tab becomes focused
   */
  useTokenValidationOnFocus(user);

  useEffect(() => {
    dispatch(getTeam());
    dispatch(getEvent());
    dispatch(getCurrentBroadcast(true));
    dispatch(getSources());
    dispatch(getBroadcastRolesWithUsers());
  }, [dispatch]);

  const pathParams = getURLPathParams();

  const teamId = pathParams?.teamId;
  const eventId = pathParams?.eventId;
  const broadcastId = pathParams?.broadcastId;

  const joinRoute = `/studio-director-embed/teams/${teamId}/events/${eventId}/broadcasts/${broadcastId}/join`;

  const ldPayload = useLDPayload();

  // Only initialize LaunchDarkly when we have complete data
  const shouldInitializeLD = ldPayload && isRequiredDataLoaded;
  const isLDInitializedOrTimedOut = useLaunchDarkly(
    shouldInitializeLD ? ldPayload : undefined,
    getEnvVar('REACT_APP_LAUNCHDARKLY_KEY')
  );

  if (!isRequiredDataLoaded || !ldPayload) return <></>;
  if (shouldInitializeLD && !isLDInitializedOrTimedOut) return <></>;

  const BroadcastPage = AgoraBroadcastPage;
  const Join = AgoraJoin;

  const metricProps = {
    accountMappingId,
    attendeeId: externalUserId,
    studioUserId: userId,
    persona: ATTRIBUTE_PERSONA_TYPE_HOST,
    meetingType: VENDOR_AGORA,
    rudderstackWriteKey: getEnvVar('REACT_APP_RUDDERSTACK_WRITE_KEY'),
    rudderstackDataPlaneUrl: getEnvVar('REACT_APP_RUDDERSTACK_DATA_PLANE_URL'),
    environmentName: getEnvironmentType(getEnvVar('REACT_APP_CVENT_ENV')),
    appVersion: version,
    broadcastId: broadcast?._id,
    teamId: broadcast?.team,
    studioEventId: broadcast?.event,
    sessionId: broadcast?.externalId,
    sourceId,
    sessionStartDate: new Date(broadcast?.startOn),
    sessionEndDate: new Date(broadcast?.endOn),
    sessionName: broadcast?.title,
    broadcastType: broadcastTypesMap.get(
      broadcast?.meta?.broadcastType?.toLowerCase()
    ),
    eventId: externalEventId,
    eventName: eventTitle,
    eventStartDate: new Date(eventStartDate),
    eventEndDate: new Date(eventEndDate)
  };

  return (
    <MetricsProvider {...metricProps}>
      <LoadReadinessCheckerSLIObserver paths={LOAD_READINESS_SLI_PATHS} />
      <AppContext.Provider value={appContextValue}>
        <BrandContext.Provider value={brandingSettings}>
          <Modals />
          <Suspense fallback={<Loading />}>
            <Route exact path={Paths.BroadcastPage}>
              {hasAttendeeCheckedReadiness ? (
                <BroadcastPage />
              ) : (
                <Redirect to={joinRoute} />
              )}
            </Route>
            <Route exact path={Paths.JoinPage} component={Join} />
          </Suspense>
          {user && broadcast && (
            <Suspense fallback={<Loading />}>
              <IntercomApp
                appId={intercomProps.appId}
                intercomIdentityVerificationKey={
                  intercomProps.intercomIdentityVerificationKey
                }
                userDetails={{
                  id: userId,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  emailAddress: user.email,
                  persona: ATTRIBUTE_PERSONA_TYPE_HOST
                }}
                eventDetails={{
                  eventId: broadcast?.event
                }}
              />
            </Suspense>
          )}
        </BrandContext.Provider>
      </AppContext.Provider>
    </MetricsProvider>
  );
}

export default App;
